import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ContentContext = createContext({});

export const ContentProvider = ({ children }) => {
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [editMode, setEditMode] = useState(() => sessionStorage.getItem("arcana_edit_mode") === "1");
  const [drafts, setDrafts] = useState({});
  const [version, setVersion] = useState(0);

  const reload = () =>
    api.get("/content").then(({ data }) => setContent(data)).catch(() => setContent({}));

  useEffect(() => {
    reload();
  }, []);

  const t = (key, fallback = "") => (content && content[key]) || fallback;
  const isAdminEditing = editMode && user?.role === "admin";

  const enterEditMode = () => {
    sessionStorage.setItem("arcana_edit_mode", "1");
    setEditMode(true);
  };

  const exitEditMode = () => {
    sessionStorage.removeItem("arcana_edit_mode");
    setEditMode(false);
    setDrafts({});
    setVersion((v) => v + 1);
  };

  const setDraft = (key, rawValue, fallback = "") => {
    const value = (rawValue || "").trim();
    setDrafts((prev) => {
      const published = (content && content[key]) || fallback;
      if (!value || value === published) {
        if (prev[key] === undefined) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  };

  const discardAll = () => {
    setDrafts({});
    setVersion((v) => v + 1);
  };

  const publishAll = async () => {
    const { data } = await api.post("/admin/content/publish", { updates: drafts });
    await reload();
    setDrafts({});
    setVersion((v) => v + 1);
    return data;
  };

  return (
    <ContentContext.Provider
      value={{ t, ready: content !== null, reload, editMode, isAdminEditing, enterEditMode, exitEditMode, drafts, setDraft, discardAll, publishAll, version }}
    >
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);
